import { useEffect, useState } from "react";
import styled from "styled-components";
import { FiHome } from "react-icons/fi";
import type { HousingSimulatorConfig } from "@dd-report-components/report/ExecutiveSummaryCockpit";
import { CiteSup, openCitation } from "./CitationSidePanel";
import { interpolate, useSimulatorText } from "./simulatorText";

// ── Layout ─────────────────────────────────────────────────────────
const SimulatorSection = styled.div`
  margin: 1.75rem 0 1.25rem;
`;

const SimulatorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 0.5rem;
`;

const PanelIconBadge = styled.span`
  flex-shrink: 0;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.56rem;
  background: #eaf5ef;
  color: #2c8c5e;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #14323b;
`;

const IntroCaption = styled.div`
  font-size: 0.81rem;
  color: #8a97a0;
  margin-bottom: 1.1rem;
  max-width: 44rem;
  line-height: 1.55;
`;

const SimulatorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  align-items: stretch;
`;

// Uppercase, muted section kicker — shared by both panels ("Parámetros de
// proyecto" / "Resultado del proyecto").
const SectionKicker = styled.div`
  font-size: 0.59rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9aa7ac;
  margin-bottom: -0.3rem;
`;

// Shared "subtexto" — the muted one-line caption under a field, a stat, or
// a chart intro. Reused everywhere in this component, per the design.
const Hint = styled.div`
  margin-top: 0.5rem;
  font-size: 0.69rem;
  color: #8a97a0;
  line-height: 1.45;
`;

// ── Left panel: inputs ─────────────────────────────────────────────
const InputPanel = styled.div`
  background: #fafbfa;
  border: 1px solid #ecefed;
  border-radius: 1rem;
  padding: 1.1rem 1.5rem 1.375rem;
  display: flex;
  flex-direction: column;
  gap: 1.375rem;
`;

const Field = styled.div``;

const FieldHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.625rem;
`;

const FieldLabel = styled.span`
  font-size: 0.78rem;
  color: #5a6c72;
`;

const FieldValue = styled.span`
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #14323b;
  white-space: nowrap;
`;

const InfoTip = styled.span`
  cursor: help;
  flex-shrink: 0;
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  border: 1px solid #c4cfc9;
  color: #8a97a0;
  font-size: 0.56rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-left: 0.3rem;

  &:hover > span,
  &:focus > span {
    display: block;
  }
`;

const InfoBubble = styled.span`
  display: none;
  position: absolute;
  bottom: 1.4rem;
  left: 50%;
  transform: translateX(-50%);
  width: 15rem;
  background: #14323b;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 400;
  line-height: 1.4;
  padding: 0.6rem 0.7rem;
  border-radius: 0.5rem;
  z-index: 10;
  cursor: default;
  white-space: normal;
`;

const NumberInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #14323b;
  border: 1px solid #e4eae7;
  border-radius: 0.56rem;
  padding: 0.56rem 0.75rem;
  background: #ffffff;
  margin-top: 0.56rem;
`;

const RangeInput = styled.input`
  width: 100%;
  accent-color: #35a46e;
  height: 4px;
  cursor: pointer;
  margin-top: 0.7rem;
`;

const SliderEndLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.625rem;
  color: #b0bcb6;
  margin-top: 0.3rem;
`;

const StepperRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
`;

const StepperButton = styled.button`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e4eae7;
  background: #ffffff;
  color: #37474d;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const StepperValue = styled.span`
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 800;
  color: #14323b;
  min-width: 1rem;
  text-align: center;
`;

const ToggleRow = styled.div`
  display: flex;
  gap: 0.625rem;
  margin-top: 0.56rem;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.56rem 0.75rem;
  border-radius: 0.56rem;
  border: 1px solid #e4eae7;
  background: ${(p) => (p.$active ? "#14323b" : "#ffffff")};
  color: ${(p) => (p.$active ? "#ffffff" : "#5a6c72")};
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
`;

// ── Right panel: results ───────────────────────────────────────────
const ResultPanel = styled.div`
  background: #ffffff;
  border: 1px solid #eef1ef;
  border-radius: 1rem;
  padding: 1.1rem 1.5rem 1.375rem;
  display: flex;
  flex-direction: column;
  gap: 1.375rem;
`;

const HeroPill = styled.div`
  background: linear-gradient(160deg, #f1f8f4, #fafbfa);
  border: 1px solid #deeee5;
  border-radius: 0.875rem;
  padding: 1.25rem 1.375rem;
`;

const HeroKicker = styled.div`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #237a50;
`;

const HeroFormula = styled.div`
  margin-top: 0.2rem;
  font-size: 0.69rem;
  color: #7c8b91;
`;

const HeroRow = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
`;

const HeroValue = styled.span`
  font-family: inherit;
  font-size: 2.75rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #14323b;
  line-height: 1;
`;

const HeroCaption = styled.span`
  font-size: 0.78rem;
  color: #55666c;
`;

const RangeNote = styled.div`
  margin-top: 0.45rem;
  font-size: 0.69rem;
  color: #7c8b91;
  line-height: 1.45;

  strong {
    color: #37474d;
    font-weight: 700;
  }
`;

const FloorsNote = styled.div`
  margin-top: 0.5rem;
  font-size: 0.72rem;
  color: #7c8b91;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
`;

const StatBox = styled.div`
  background: #fafbfa;
  border: 1px solid #ecefed;
  border-radius: 0.75rem;
  padding: 0.81rem 0.94rem;
`;

const StatLabel = styled.div`
  font-size: 0.5625rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #9aa7ac;
`;

const StatValue = styled.div`
  margin-top: 0.375rem;
  font-family: inherit;
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #14323b;
  line-height: 1;
`;

const HabitabilityRow = styled.div<{ $ok: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 0.94rem;
  border-radius: 0.75rem;
  background: ${(p) => (p.$ok ? "#eaf5ef" : "#fbeee8")};
  border: 1px solid ${(p) => (p.$ok ? "#d4eadd" : "#f2d9ce")};
`;

const HabitabilityValue = styled.span`
  display: block;
  margin-top: 0.3rem;
  font-family: inherit;
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #14323b;
  line-height: 1;
`;

const HabitabilityPill = styled.span<{ $ok: boolean }>`
  flex-shrink: 0;
  padding: 0.3rem 0.75rem;
  border-radius: 100px;
  font-size: 0.69rem;
  font-weight: 700;
  background: #ffffff;
  color: ${(p) => (p.$ok ? "#237a50" : "#c0392b")};
  border: 1px solid ${(p) => (p.$ok ? "#d4eadd" : "#f2d9ce")};
`;

const SensIntroTitle = styled.div`
  font-size: 0.81rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #14323b;
  margin-bottom: 0.25rem;
`;

const SensChart = styled.div`
  margin-top: 0.7rem;
  display: flex;
  align-items: flex-end;
  gap: 0.625rem;
  height: 6rem;
`;

const SensBar = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 0.375rem;
  height: 100%;
`;

const SensCount = styled.div<{ $current: boolean }>`
  font-size: 0.69rem;
  font-weight: 700;
  color: ${(p) => (p.$current ? "#14323b" : "#8a979c")};
`;

const SensBarFill = styled.div<{ $height: string; $current: boolean }>`
  width: 100%;
  height: ${(p) => p.$height};
  border-radius: 0.3rem 0.3rem 0 0;
  background: ${(p) => (p.$current ? "#35a46e" : "#cfe3d8")};
`;

const SensLabel = styled.div`
  font-size: 0.625rem;
  color: #9aa7ac;
`;

const SensCaption = styled.div`
  margin-top: 0.375rem;
  font-size: 0.625rem;
  color: #b0bcb6;
  text-align: center;
`;

const Disclaimer = styled.div`
  margin-top: 0.75rem;
  font-size: 0.66rem;
  color: #a2afb4;
  line-height: 1.55;
`;

interface Props {
  initialPlotAreaM2?: number;
  initialOccupancyPct?: number;
  maxFloors?: number;
  // The property's own planning rules and wording; absent, Torrent's.
  config?: HousingSimulatorConfig;
  // Lets the valuation simulator sync its "edificabilidad vendible" to this
  // simulator's residential floor area, matching the design's two-way link.
  // The ground floor travels along: turned into housing, it takes the shop's
  // fixed revenue out of the valuation.
  onResidentialFloorAreaChange?: (value: number, groundFloorIsHousing?: boolean) => void;
}

const SENSITIVITY_SIZES = [70, 85, 100, 120, 145];
const DEFAULT_UNIT_SIZE_RANGE = { min: 85, max: 120 };
const DEFAULT_OCCUPANCY_RANGE = { min: 40, max: 100, step: 1 };
const DEFAULT_UNIT_SIZE_SLIDER = { min: 45, max: 160, step: 5 };
const DEFAULT_MIN_USABLE_M2 = 30;

const fmtN = (n: number) => Math.round(n).toLocaleString("es-ES");

// Client-side calculator — no backend data behind it beyond the seed values
// (plot surface, occupancy), which come from cadastral/urbanism data already
// in the report. Formulas match the design's reference implementation
// 1:1 (dwHuella/dwTecho/dwViv/dwUtil/dwSens in Rediseno_DD_v5.dc.html).
export const HousingSimulator = ({
  initialPlotAreaM2 = 1630,
  initialOccupancyPct = 62,
  maxFloors = 6,
  config,
  onResidentialFloorAreaChange,
}: Props) => {
  const { text, rich, has } = useSimulatorText("dueDiligence.finalReport.cockpit.housingSimulator", config?.texts);
  const minFloors = config?.minFloors ?? 1;
  const occupancyRange = config?.ranges?.occupancy ?? DEFAULT_OCCUPANCY_RANGE;
  const unitSizeSlider = config?.ranges?.avgUnitSize ?? DEFAULT_UNIT_SIZE_SLIDER;
  const unitSizeRange = config?.unitSizeRange ?? DEFAULT_UNIT_SIZE_RANGE;
  const sensitivitySizes = config?.sensitivitySizes ?? SENSITIVITY_SIZES;
  const minUsable = config?.minUsableM2 ?? DEFAULT_MIN_USABLE_M2;
  const envelope = config?.envelope;
  const cite = (slot: keyof NonNullable<HousingSimulatorConfig["cites"]>, fallback: string) => {
    const key = config?.cites?.[slot] ?? fallback;
    return <CiteSup onClick={() => openCitation(key)} />;
  };

  const [plotArea, setPlotArea] = useState(initialPlotAreaM2);
  const [occupancyPct, setOccupancyPct] = useState(initialOccupancyPct);
  const [floors, setFloors] = useState(config?.initialFloors ?? maxFloors);
  const [groundFloorIsHousing, setGroundFloorIsHousing] = useState(false);
  const [avgUnitSize, setAvgUnitSize] = useState(config?.initialAvgUnitSize ?? 100);
  const [usableRatio, setUsableRatio] = useState(config?.initialUsableRatio ?? 0.8);

  const residentialFloors = Math.max(0, groundFloorIsHousing ? floors : floors - 1);
  const footprint = (plotArea * occupancyPct) / 100;
  // Under an envelope ordinance the ground floor is inside the capped total,
  // and a shop there is taken out of it rather than costing a whole floor.
  const maxBuiltArea = envelope ? plotArea * envelope.maxRatio : Infinity;
  const totalFloorArea = envelope
    ? Math.min(footprint * (floors === envelope.fullAtFloors ? envelope.maxRatio : floors), maxBuiltArea)
    : footprint * residentialFloors;
  const residentialFloorArea = envelope
    ? Math.max(0, totalFloorArea - (groundFloorIsHousing ? 0 : Math.min(footprint, envelope.groundFloorCommercialM2)))
    : totalFloorArea;
  // One dwelling per `densityM2PerUnit` m²t caps every count below.
  const densityBase = config?.densityOverTotal ? totalFloorArea : residentialFloorArea;
  const densityCap = config?.densityM2PerUnit ? Math.round(densityBase / config.densityM2PerUnit) : Infinity;
  const capped = (n: number) => Math.min(densityCap, n);

  useEffect(() => {
    onResidentialFloorAreaChange?.(residentialFloorArea, groundFloorIsHousing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentialFloorArea, groundFloorIsHousing]);
  const units = Math.max(0, capped(Math.floor(residentialFloorArea / avgUnitSize)));
  const usableArea = avgUnitSize * usableRatio;
  const meetsHabitability = usableArea >= minUsable;
  const rangeLow = capped(Math.floor(residentialFloorArea / unitSizeRange.max));
  const rangeHigh = capped(Math.floor(residentialFloorArea / unitSizeRange.min));
  const commercialSpaces =
    config?.parkingPerCommercialM2 && !groundFloorIsHousing
      ? Math.ceil((envelope?.groundFloorCommercialM2 ?? 0) / config.parkingPerCommercialM2)
      : 0;
  // Figures a report's own wording may quote.
  const vars = {
    max: maxFloors,
    cap: Number.isFinite(densityCap) ? fmtN(densityCap) : "",
    maxBuiltArea: Number.isFinite(maxBuiltArea) ? fmtN(maxBuiltArea) : "",
    standard: fmtN(units + commercialSpaces),
  };

  const sensMax = Math.max(...sensitivitySizes.map((m) => capped(residentialFloorArea / m)), 1);
  const sensitivity = sensitivitySizes.map((m) => {
    const n = Math.max(0, capped(Math.floor(residentialFloorArea / m)));
    const current = Math.abs(m - avgUnitSize) < 2.5;
    return { m, n, height: Math.max(4, (n / sensMax) * 100).toFixed(1) + "%", current };
  });

  return (
    <SimulatorSection>
      <SimulatorHeader>
        <PanelIconBadge>
          <FiHome size={15} />
        </PanelIconBadge>
        <PanelTitle>{text("title", vars)}</PanelTitle>
      </SimulatorHeader>
      <IntroCaption>
        {rich("intro", vars, { 1: cite("intro", "pgou") })}
      </IntroCaption>

      <SimulatorGrid>
        <InputPanel>
          <SectionKicker>{text("paramsTitle", vars)}</SectionKicker>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {rich("plotAreaLabel", vars, { 1: cite("plotArea", "cat") })}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{text("plotAreaTip", vars)}</InfoBubble>
                </InfoTip>
              </FieldLabel>
            </FieldHeader>
            <NumberInput
              type="number"
              step={10}
              value={plotArea}
              readOnly={config?.plotAreaFixed}
              onChange={(e) => setPlotArea(+e.target.value || 0)}
            />
            <Hint>{text("plotAreaHint", vars)}</Hint>
          </Field>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {rich("occupancyLabel", vars, { 1: cite("occupancy", "urb") })}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{text("occupancyTip", vars)}</InfoBubble>
                </InfoTip>
              </FieldLabel>
              <FieldValue>{occupancyPct} %</FieldValue>
            </FieldHeader>
            <RangeInput
              type="range"
              min={occupancyRange.min}
              max={occupancyRange.max}
              step={occupancyRange.step}
              value={occupancyPct}
              onChange={(e) => setOccupancyPct(+e.target.value)}
            />
            <SliderEndLabels>
              <span>{text("occupancyMinLabel", vars)}</span>
              <span>{text("occupancyMaxLabel", vars)}</span>
            </SliderEndLabels>
            <Hint>{text("occupancyHint", vars)}</Hint>
          </Field>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {rich("floorsLabel", vars, { 1: cite("floors", "urb") })}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{text("floorsTip", vars)}</InfoBubble>
                </InfoTip>
              </FieldLabel>
              <StepperRow>
                <StepperButton
                  type="button"
                  disabled={floors <= minFloors}
                  onClick={() => setFloors((f) => Math.max(minFloors, f - 1))}
                >
                  −
                </StepperButton>
                <StepperValue>{floors}</StepperValue>
                <StepperButton
                  type="button"
                  disabled={floors >= maxFloors}
                  onClick={() => setFloors((f) => Math.min(maxFloors, f + 1))}
                >
                  +
                </StepperButton>
              </StepperRow>
            </FieldHeader>
            <Hint>{text("floorsHint", vars)}</Hint>
          </Field>

          <Field>
            <FieldLabel>
              {text("groundFloorLabel", vars)}
              <InfoTip tabIndex={0}>
                i<InfoBubble>{text("groundFloorTip", vars)}</InfoBubble>
              </InfoTip>
            </FieldLabel>
            <ToggleRow>
              <ToggleButton type="button" $active={!groundFloorIsHousing} onClick={() => setGroundFloorIsHousing(false)}>
                {text("groundFloorPortal", vars)}
              </ToggleButton>
              <ToggleButton type="button" $active={groundFloorIsHousing} onClick={() => setGroundFloorIsHousing(true)}>
                {text("groundFloorHousing", vars)}
              </ToggleButton>
            </ToggleRow>
            <Hint>{text(groundFloorIsHousing && has("groundFloorHintHousing") ? "groundFloorHintHousing" : "groundFloorHint", vars)}</Hint>
          </Field>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {text("avgUnitSizeLabel", vars)}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{text("avgUnitSizeTip", vars)}</InfoBubble>
                </InfoTip>
              </FieldLabel>
              <FieldValue>{avgUnitSize} m²</FieldValue>
            </FieldHeader>
            <RangeInput
              type="range"
              min={unitSizeSlider.min}
              max={unitSizeSlider.max}
              step={unitSizeSlider.step}
              value={avgUnitSize}
              onChange={(e) => setAvgUnitSize(+e.target.value)}
            />
            <SliderEndLabels>
              <span>{text("avgUnitSizeMinLabel", vars)}</span>
              <span>{text("avgUnitSizeMaxLabel", vars)}</span>
            </SliderEndLabels>
            <Hint>{text("avgUnitSizeHint", vars)}</Hint>
          </Field>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {rich("usableRatioLabel", vars, { 1: cite("usableRatio", "hab") })}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{text("usableRatioTip", vars)}</InfoBubble>
                </InfoTip>
              </FieldLabel>
              <FieldValue>{usableRatio.toFixed(2)}</FieldValue>
            </FieldHeader>
            <RangeInput
              type="range"
              min={0.65}
              max={0.9}
              step={0.01}
              value={usableRatio}
              onChange={(e) => setUsableRatio(+e.target.value)}
            />
            <SliderEndLabels>
              <span>{text("usableRatioMinLabel", vars)}</span>
              <span>{text("usableRatioMaxLabel", vars)}</span>
            </SliderEndLabels>
            <Hint>{text("usableRatioHint", vars)}</Hint>
          </Field>
        </InputPanel>

        <ResultPanel>
          <SectionKicker>{text("resultTitle", vars)}</SectionKicker>

          <HeroPill>
            <HeroKicker>{text("heroKicker", vars)}</HeroKicker>
            <HeroFormula>{text("heroFormula", vars)}</HeroFormula>
            <HeroRow>
              <HeroValue>{fmtN(units)}</HeroValue>
              <HeroCaption>{text("currentAssumptions", vars)}</HeroCaption>
            </HeroRow>
            <RangeNote>
              {rich("rangeNote", { ...vars, low: fmtN(rangeLow), high: fmtN(rangeHigh) }, { 1: <strong /> })}
            </RangeNote>
            <FloorsNote>
              {text("floorsSummary", { ...vars, floors: fmtN(residentialFloors), footprint: fmtN(footprint) })}
            </FloorsNote>
          </HeroPill>

          <StatGrid>
            <StatBox>
              <StatLabel>{text("residentialFloorAreaLabel", vars)}</StatLabel>
              <StatValue>{fmtN(residentialFloorArea)} m²</StatValue>
              <Hint>{text("residentialFloorAreaHint", vars)}</Hint>
            </StatBox>
            <StatBox>
              <StatLabel>
                {rich("parkingLabel", vars, { 1: cite("parking", "urb") })}
              </StatLabel>
              <StatValue>{config?.parkingValue != null ? interpolate(config.parkingValue, vars) : vars.standard}</StatValue>
              <Hint>{text("parkingHint", vars)}</Hint>
            </StatBox>
          </StatGrid>

          <div>
            <HabitabilityRow $ok={meetsHabitability}>
              <span>
                <StatLabel>
                  {rich("usableAreaLabel", vars, { 1: cite("usableArea", "hab") })}
                </StatLabel>
                <HabitabilityValue>{fmtN(usableArea)} m²</HabitabilityValue>
              </span>
              <HabitabilityPill $ok={meetsHabitability}>
                {meetsHabitability
                  ? text("habitabilityOk", vars)
                  : text("habitabilityFail", vars)}
              </HabitabilityPill>
            </HabitabilityRow>
            <Hint>{text("usableAreaHint", vars)}</Hint>
          </div>

          <div>
            <SensIntroTitle>{text("sensitivityTitle", vars)}</SensIntroTitle>
            <Hint style={{ marginTop: 0 }}>{text("sensitivityIntro", vars)}</Hint>
            <SensChart>
              {sensitivity.map((s, i) => (
                <SensBar key={i}>
                  <SensCount $current={s.current}>{fmtN(s.n)}</SensCount>
                  <SensBarFill $height={s.height} $current={s.current} />
                  <SensLabel>{s.m}</SensLabel>
                </SensBar>
              ))}
            </SensChart>
            <SensCaption>{text("sensitivityCaption", vars)}</SensCaption>
          </div>
        </ResultPanel>
      </SimulatorGrid>

      <Disclaimer>{rich("disclaimer", vars, { 1: cite("disclaimer", "urb") })}</Disclaimer>
    </SimulatorSection>
  );
};
